<template>
  <div class="mobile-betting-section">
    <div class="mobile-betting-header">
      <h3 class="mobile-section-title">
        {{ $t('betting.selectYourMarket') }}
      </h3>
    </div>
    
    <!-- Tab Navigation -->
    <div class="mobile-tab-navigation">
      <button
        class="mobile-tab-btn"
        :class="{ active: activeTab === 'main' }"
        @click="activeTab = 'main'"
      >
        🎯 {{ $t('betting.mainMarkets') }}
      </button>
      <button
        class="mobile-tab-btn"
        :class="{ active: activeTab === 'bonus' }"
        @click="activeTab = 'bonus'"
      >
        🎁 {{ $t('betting.bonusMarkets') }}
      </button>
    </div>
    
    <!-- Main Tab Content -->
    <div
      v-if="activeTab === 'main'"
      class="mobile-tab-content active-tab-content"
    >
      <div class="mobile-market-dropdown-section">
        <div class="mobile-dropdown-container">
          <label class="mobile-dropdown-label">{{ $t('betting.selectMarket') }}:</label>
          <select 
            v-model="selectedMainMarket"
            class="mobile-market-dropdown"
            @change="onMainMarketChange"
          >
            <option 
              v-for="market in allMainMarkets" 
              :key="market.id"
              :value="market.id"
            >
              {{ getMarketDisplayLabel(market) }}
            </option>
          </select>
        </div>
      </div>
      
      <!-- Number Selection Section -->
      <div
        v-if="selectedMainMarketData && showMainNumberSelection"
        class="mobile-number-selection-section"
      >
        <div class="mobile-number-selection-header">
          <span class="mobile-number-selection-title">{{ $t('betting.selectNumbers') }}</span>
          <span
            v-if="currentOdds && selectedNumbers.length > 0"
            class="mobile-number-selection-odds"
          >{{ $t('betting.odds') }}: {{ formatOdds(currentOdds) }}</span>
        </div>

        <!-- Main Number Grid -->
        <div
          v-if="!isBonusOnlyMarket && !isMainIncludingBonusMarket"
          class="mobile-number-grid"
        >
          <div
            v-for="n in actualBallsMax"
            :key="n"
            class="mobile-number-circle"
            :class="{
              selected: selectedNumbers.includes(n),
              hot: hotNumbers?.includes(n),
              cold: coldNumbers?.includes(n),
              disabled: (isSameDrumMarket) && selectedBonusNumbers.includes(n),
              'quick-pick': selectedNumbers.includes(n) && selectedNumbers.length === actualBalls,
            }"
            @click="toggleNumber(n)"
          >
            {{ n }}
          </div>
        </div>

        <!-- Main Including Bonus Number Grid (Single Grid) -->
        <div
          v-if="isMainIncludingBonusMarket"
          class="mobile-number-grid"
        >
          <div
            v-for="n in actualBallsMax"
            :key="n"
            class="mobile-number-circle"
            :class="{
              selected: selectedNumbers.includes(n),
              hot: hotNumbers?.includes(n),
              cold: coldNumbers?.includes(n),
              'quick-pick': selectedNumbers.includes(n) && selectedNumbers.length >= 1,
            }"
            @click="toggleMainIncludingBonusNumber(n)"
          >
            {{ n }}
          </div>
        </div>

        <!-- Bonus ball colour (UK49 color combo) -->
        <div
          v-if="isColorComboMarket"
          class="mobile-bonus-section mobile-color-combo-section"
        >
          <BonusBallColorRow v-model="selectedBonusColor" />
        </div>

        <!-- Bonus Ball Selection -->
        <div
          v-if="(requiresBonusBall || isSameDrumMarket || isBonusOnlyMarket) && !isMainIncludingBonusMarket && !isColorComboMarket"
          class="mobile-bonus-section"
        >
          <div class="mobile-bonus-title">
            {{ isSameDrumBonusMarket ? '' : (isBonusOnlyMarket ? $t('betting.selectBonusNumber') : $t('betting.bonusNumber')) }}
          </div>
          <div
            v-if="!isSameDrumBonusMarket || isBonusOnlyMarket"
            class="mobile-bonus-number-grid"
          >
            <div
              v-for="n in bonusBallsMax"
              :key="`bonus-${n}`"
              class="mobile-number-circle mobile-bonus-circle"
              :class="{
                selected: selectedBonusNumbers.includes(n),
                hot: hotNumbers?.includes(n),
                cold: coldNumbers?.includes(n),
                disabled: (isSameDrumMarket) && selectedNumbers.includes(n),
              }"
              @click="toggleBonusNumber(n)"
            >
              {{ n }}
            </div>
          </div>
        </div>

        <!-- Selected Market Info -->
        <div class="mobile-market-info">
          <div
            class="mobile-market-display"
            :class="{ 'center-layout': !((isBonusOnlyMarket && selectedBonusNumbers.length > 0) || (!isBonusOnlyMarket && selectedNumbers.length > 0)) }"
          >
            <span class="mobile-market-text">
              <template v-if="isMainIncludingBonusMarket">
                <template v-if="selectedNumbers.length === 0">
                  {{ $t('betting.pleaseSelectNumbers') }}
                </template>
                <template v-else>
                  {{ $t('betting.selectedBalls', { count: selectedNumbers.length }) }}
                </template>
              </template>
              <template v-else-if="(isBonusOnlyMarket && selectedBonusNumbers.length > 0) || (!isBonusOnlyMarket && selectedNumbers.length > 0)">
                {{ isBonusOnlyMarket 
                  ? $t('betting.selectedBonusBalls', { count: selectedBonusNumbers.length })
                  : $t('betting.selectedBalls', { count: selectedNumbers.length })
                }}
              </template>
              <template v-else>
                {{ $t('betting.pleaseSelectNumber') }}
              </template>
            </span>
            <span
              v-if="selectedNumbers.length > 0 || (isColorComboMarket && selectedBonusColor)"
              class="mobile-market-odds"
            >{{ formatOdds(currentOdds) }}</span>
          </div>
        </div>

        <div
          v-if="hasAnySelection"
          class="mobile-betting-actions-primary"
        >
          <button
            type="button"
            class="mobile-betting-action-btn-hero"
            :disabled="!canAddLine"
            @click="addLine"
          >
            {{ props.streamlinedSelection ? $t('detail.addToBetslip') : $t('betting.addLine') }}
          </button>
        </div>
        <div class="mobile-betting-actions-secondary">
          <BettingPickActionButton
            variant="quick"
            platform="mobile"
            :pick-options="availablePickCounts"
            :selected-count="quickPickCount"
            :active="lastPickAction === 'quick' && hasAnySelection"
            @update:selected-count="quickPickCount = $event"
            @action="quickPick"
          >
            <template #icon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.1"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M16 3h5v5" />
                <path d="m8 16-5 5" />
                <path d="M21 3l-7 7" />
                <path d="M3 8V3h5" />
                <path d="m3 3 7 7" />
                <path d="m14 14 7 7" />
              </svg>
            </template>
            {{ $t('betting.quickPick') }}
          </BettingPickActionButton>
          <BettingPickActionButton
            v-if="shouldShowHotColdActions"
            variant="hot"
            platform="mobile"
            :pick-options="availablePickCounts"
            :selected-count="hotPickCount"
            :active="lastPickAction === 'hot' && hasAnySelection"
            @update:selected-count="hotPickCount = $event"
            @action="selectHotNumbers"
          >
            <template #icon>
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12.7 2.6c.3 2-.3 3.5-1.7 5.2-1 1.2-1.6 2.2-1.6 3.7 0 1.8 1.3 3.1 3 3.1 2.3 0 3.8-1.9 3.8-4.3 0-1.1-.3-2.1-1.1-3.5 2.9 1.6 4.8 4.4 4.8 7.8 0 4.2-3.2 7.4-7.7 7.4-4.1 0-7.2-2.9-7.2-6.9 0-2.8 1.4-4.9 3.6-6.9 1.8-1.7 3-3.1 4.1-5.6z" />
              </svg>
            </template>
            {{ $t('betting.hotNumbers') }}
          </BettingPickActionButton>
          <BettingPickActionButton
            v-if="shouldShowHotColdActions"
            variant="cold"
            platform="mobile"
            :pick-options="availablePickCounts"
            :selected-count="coldPickCount"
            :active="lastPickAction === 'cold' && hasAnySelection"
            @update:selected-count="coldPickCount = $event"
            @action="selectColdNumbers"
          >
            <template #icon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.9"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 2v20" />
                <path d="m4.9 6.1 14.2 11.8" />
                <path d="m19.1 6.1-14.2 11.8" />
                <path d="M4 12h16" />
              </svg>
            </template>
            {{ $t('betting.coldNumbers') }}
          </BettingPickActionButton>
          <button
            type="button"
            class="mobile-betting-action-btn-secondary mobile-betting-action-btn-secondary--clear"
            @click="clearAll"
          >
            <span
              class="betting-action-btn-icon"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M8 6V4h8v2" />
                <path d="m19 6-1 14H6L5 6" />
              </svg>
            </span>
            <span>{{ $t('common.clearAll') }}</span>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Bonus Market Tab Content -->
    <div
      v-if="activeTab === 'bonus'"
      class="mobile-tab-content active-tab-content"
    >
      <div class="mobile-bonus-markets">
        <div
          v-for="group in bonusMarkets"
          :key="group.id"
          class="mobile-bonus-group"
        >
          <div class="mobile-bonus-group-title">
            {{ group.markets[0] ? getMarketDisplayLabel(group.markets[0]) : group.name }}
          </div>
          
          <div class="mobile-bonus-markets-list">
            <template
              v-for="market in group.markets"
              :key="market.id"
            >
              <SameColorBallsSelector
                v-if="isSameColorBallsMarket(market.slug)"
                :market="market"
                class="mobile-same-color-selector"
                @select="(outcome) => selectSameColorBalls(market, outcome, group)"
              />
              <div
                v-else-if="market.outcomes && market.outcomes.length > 0"
                class="mobile-bonus-market-item"
              >
                <div class="mobile-bonus-market-description">
                  {{ formatMarketDescription(market) }}
                </div>
                <div class="mobile-bonus-options">
                  <div
                    v-for="outcome in market.outcomes"
                    :key="outcome.id"
                    class="mobile-bonus-bet-card"
                    :class="[
                      { selected: isMarketSelected(market, outcome) },
                      getBonusColorClass(outcome)
                    ]"
                    :style="getBonusColorStyle(outcome)"
                    @click="selectBonusMarket(market, outcome, group)"
                  >
                    <div class="mobile-bonus-bet-content">
                      <div class="mobile-bonus-bet-label">
                        {{ formatOutcomeText(outcome) }}
                      </div>
                      <div class="mobile-bonus-bet-odds">
                        {{ formatOdds(Number(outcome.odds)) }}
                      </div>
                    </div>
                    <div class="mobile-bonus-bet-indicator">
                      <div class="mobile-bonus-bet-check">
                        ✓
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, watch } from 'vue';
import { formatOdds } from '../../utils/currency';
import { getMarketDisplayLabel } from '../../utils/market';
import SameColorBallsSelector from '../SameColorBallsSelector.vue';
import BonusBallColorRow from '../BonusBallColorRow.vue';
import BettingPickActionButton from '../betting/BettingPickActionButton.vue';
import { getAvailablePickCounts, resolveDefaultPickCount, clampToAvailablePickCount } from '@/composables/useAvailablePickCounts';
import {
  isSameColorBallsMarket,
  formatSameColorBetText,
  buildSameColorMarketSelection,
  pickRandomBallColor,
  type BallColorSlug,
} from '@/constants/sameColorBalls';
import {
  isStandardBetPlusColorBonusMarket,
  buildStandardBetPlusColorBonusSelection,
  resolveColorBonusOutcome,
} from '@/constants/standardBetPlusColorBonus';
import { useIntegrationMode } from '@/composables/useIntegrationMode';

interface MarketOutcome {
  id: number;
  game_market_id?: number;
  text: string;
  select_numbers: boolean;
  pick: number;
  slug: string;
  odds: string;
  specifier: string | null;
}

interface Market {
  id: number;
  name: string;
  slug: string;
  description: string;
  ordering: number;
  outcomes: MarketOutcome[];
}

interface MarketGroup {
  id: number;
  name: string;
  slug: string;
  ordering: number;
  has_bonus: boolean;
  markets: Market[];
  rules: Array<{
    id: number;
    name: string;
    balls_min: number;
    balls_max: number;
    picks_min: number;
    picks_max: number;
    has_bonus: number | boolean;
    max_resulted: number;
    type: string;
  }>;
}

interface SelectedMarket {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
  specifier?: string;
  game_market_id?: number;
}

interface Props {
  bettingMarkets: MarketGroup[];
  selectedMarket: SelectedMarket | null;
  hotNumbers?: number[];
  coldNumbers?: number[];
  betslipLength: number;
  streamlinedSelection?: boolean;
  enableHotColdNumbers?: boolean;
}

const props = defineProps<Props>();
const { isLite } = useIntegrationMode();

const emit = defineEmits<{
  'select-market': [market: SelectedMarket | null];
  'remove-market': [market: SelectedMarket];
  'toggle-group': [index: number];
  'tab-changed': [tab: 'main' | 'bonus'];
  'add-line': [line: { numbers: number[]; bonus?: number; numBets: number; picks: number; market?: SelectedMarket }];
  'remove-line': [index: number];
  'add-to-betslip': [lines: Array<{ numbers: number[]; bonus?: number; numBets: number; picks: number; market?: SelectedMarket }>];
  'clear-all': [];
}>();

// Tab state
const activeTab = ref<'main' | 'bonus'>('main');

// Main market selection
const selectedMainMarket = ref<number | ''>('');
const selectedBonusMarkets = ref<Array<{ market: Market; outcome: MarketOutcome }>>([]);

// Number selection state
const selectedNumbers = ref<number[]>([]);
const selectedBonusNumbers = ref<number[]>([]);
const selectedBonusColor = ref<BallColorSlug | null>(null);
const lines = ref<Array<{ numbers: number[]; bonus?: number; numBets: number; picks: number; market?: SelectedMarket }>>([]);

// Quick pick pattern memory
const lastQuickPickPattern = ref<{ mainCount: number; bonusCount: number } | null>(null);

// Computed properties to separate markets
const mainMarkets = computed(() => {
  return props.bettingMarkets.filter(group => 
    group.markets.some(market => 
      market.outcomes.some(outcome => outcome.select_numbers) ||
      isStandardBetPlusColorBonusMarket(market.slug)
    )
  );
});

const bonusMarkets = computed(() => {
  return props.bettingMarkets.map(group => ({
    ...group,
    markets: group.markets.filter(market => 
      market.outcomes.every(outcome => !outcome.select_numbers) &&
      !isStandardBetPlusColorBonusMarket(market.slug)
    )
  })).filter(group => group.markets.length > 0);
});

// Flatten all main markets for dropdown
const allMainMarkets = computed(() => {
  const markets: Market[] = [];
  mainMarkets.value.forEach(group => {
    group.markets.forEach(market => {
      if (market.outcomes.some(outcome => outcome.select_numbers)) {
        markets.push(market);
      } else if (isStandardBetPlusColorBonusMarket(market.slug)) {
        markets.push(market);
      }
    });
  });
  return markets.sort((a, b) => {
    const isABonusBallSameDrum = a.slug === 'bonus-ball-bet-same-drum-as-standard-numbers';
    const isBBonusBallSameDrum = b.slug === 'bonus-ball-bet-same-drum-as-standard-numbers';
    if (isABonusBallSameDrum && !isBBonusBallSameDrum) return 1;
    if (!isABonusBallSameDrum && isBBonusBallSameDrum) return -1;
    return 0;
  });
});

// Get selected market data
const selectedMainMarketData = computed(() => {
  if (!selectedMainMarket.value) return null;
  return allMainMarkets.value.find(market => market.id === selectedMainMarket.value);
});

// Get market group for selected market
const selectedMainMarketGroup = computed(() => {
  if (!selectedMainMarket.value) return null;
  return mainMarkets.value.find(group => 
    group.markets.some(market => market.id === selectedMainMarket.value)
  );
});

// Color combo market (standard bet + bonus colour)
const isColorComboMarket = computed(() =>
  isStandardBetPlusColorBonusMarket(selectedMainMarketData.value?.slug ?? '')
);

const showMainNumberSelection = computed(() => {
  if (!selectedMainMarketData.value) return false;
  if (isColorComboMarket.value) return true;
  return selectedMainMarketData.value.outcomes.some((outcome) => outcome.select_numbers);
});

// Number selection computed properties
const actualBallsMax = computed(() => {
  if (selectedMainMarketGroup.value?.rules?.length) {
    const mainRule = selectedMainMarketGroup.value.rules.find(rule => rule.type === 'main' || !rule.has_bonus);
    return mainRule ? mainRule.balls_max : 49;
  }
  return 49; // Default
});

const actualBalls = computed(() => {
  if (isColorComboMarket.value) {
    const picks = selectedMainMarketData.value?.outcomes
      .map((o) => Number(o.pick) || 0)
      .filter((p) => p > 0) ?? [];
    return picks.length > 0 ? Math.min(...picks) : 1;
  }
  if (selectedMainMarketData.value?.outcomes) {
    const firstOutcome = selectedMainMarketData.value.outcomes.find(outcome => outcome.select_numbers);
    return firstOutcome ? firstOutcome.pick : 5;
  }
  return 5; // Default
});

const bonusBallsMax = computed(() => {
  if (isSameDrumMarket.value) {
    return actualBallsMax.value;
  }
  if (selectedMainMarketGroup.value?.rules?.length) {
    const bonusRule = selectedMainMarketGroup.value.rules.find(rule => rule.has_bonus === 1);
    return bonusRule ? bonusRule.balls_max : 20;
  }
  return 20; // Default
});

const maxSelectableBalls = computed(() => {
  if (selectedMainMarketData.value?.outcomes) {
    const picks = selectedMainMarketData.value.outcomes
      .map((outcome) => Number(outcome.pick) || 0)
      .filter((pick) => pick > 0);
    if (picks.length > 0) {
      return Math.max(...picks);
    }
    const maxPick = Math.max(...selectedMainMarketData.value.outcomes
      .filter(outcome => outcome.select_numbers)
      .map(outcome => outcome.pick)
    );
    return maxPick || actualBallsMax.value;
  }
  return actualBallsMax.value;
});

const comboMaxPick = computed(() => {
  if (!selectedMainMarketData.value) return 5;
  const picks = selectedMainMarketData.value.outcomes
    .map((o) => Number(o.pick) || 0)
    .filter((p) => p > 0);
  return picks.length > 0 ? Math.max(...picks) : 5;
});

const requiresBonusBall = computed(() => {
  return selectedMainMarketGroup.value?.has_bonus || false;
});

const isBonusOnlyMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'bonus-ball-only-same-drum' || 
         slug === 'bonus-ball-only-additional-drum';
});

const availablePickCounts = computed(() =>
  getAvailablePickCounts(
    selectedMainMarketData.value,
    isBonusOnlyMarket.value,
    actualBalls.value
  )
);

const quickPickCount = ref(5);
const hotPickCount = ref(5);
const coldPickCount = ref(5);
const lastPickAction = ref<'quick' | 'hot' | 'cold' | null>(null);

const clearPickAction = () => {
  lastPickAction.value = null;
};

const syncPickCounts = () => {
  const defaultCount = resolveDefaultPickCount(availablePickCounts.value, actualBalls.value);
  quickPickCount.value = clampToAvailablePickCount(quickPickCount.value, availablePickCounts.value) || defaultCount;
  hotPickCount.value = clampToAvailablePickCount(hotPickCount.value, availablePickCounts.value) || defaultCount;
  coldPickCount.value = clampToAvailablePickCount(coldPickCount.value, availablePickCounts.value) || defaultCount;
};

watch([availablePickCounts, actualBalls], syncPickCounts, { immediate: true });

const isSameDrumMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'standard-bet-and-bonus-ball-same-drum' ||
         slug === 'bonus-ball-only-same-drum' ||
         slug === 'standard-bet-bonus-from-same-drum' ||
         slug === 'regular-numbers-plus-bonus-ball-same-drum';
});

const isMainIncludingBonusMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'standard-bet-bonus-from-same-drum';
});

const isSameDrumBonusMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'bonus-ball-only-same-drum' ||
         slug === 'standard-bet-bonus-from-same-drum';
});

const hasAnySelection = computed(() => {
  return selectedNumbers.value.length > 0 || selectedBonusNumbers.value.length > 0;
});

const canAddLine = computed(() => {
  if (isColorComboMarket.value) {
    return selectedNumbers.value.length > 0 && selectedBonusColor.value !== null;
  }

  if (isBonusOnlyMarket.value) {
    return selectedBonusNumbers.value.length > 0 && Boolean(currentOutcome.value);
  }
  
  if (isMainIncludingBonusMarket.value) {
    return selectedNumbers.value.length >= 1 && Boolean(currentOutcome.value);
  }
  
  if (isSameDrumMarket.value && !isMainIncludingBonusMarket.value) {
    const hasDuplicates = selectedNumbers.value.some(mainNum => 
      selectedBonusNumbers.value.includes(mainNum)
    );
    if (hasDuplicates) {
      return false;
    }
    
    return selectedNumbers.value.length > 0 && selectedBonusNumbers.value.length > 0 && Boolean(currentOutcome.value);
  }
  
  if (requiresBonusBall.value && !isSameDrumMarket.value) {
    return selectedNumbers.value.length > 0 && selectedBonusNumbers.value.length > 0 && Boolean(currentOutcome.value);
  }
  return selectedNumbers.value.length > 0 && Boolean(currentOutcome.value);
});

// Computed properties for current odds display
const currentOutcome = computed(() => {
  if (isBonusOnlyMarket.value && selectedBonusNumbers.value.length > 0) {
    return selectedMainMarketData.value?.outcomes.find(outcome => 
      outcome.select_numbers && outcome.pick === selectedBonusNumbers.value.length
    );
  }

  if (!selectedMainMarketData.value || selectedNumbers.value.length === 0) return null;

  if (isColorComboMarket.value && selectedBonusColor.value) {
    return resolveColorBonusOutcome(
      selectedMainMarketData.value.outcomes,
      selectedBonusColor.value,
      selectedNumbers.value.length
    ) ?? null;
  }
  
  // For main including bonus markets, use the actual number count
  if (isMainIncludingBonusMarket.value) {
    return selectedMainMarketData.value.outcomes.find(outcome => 
      outcome.select_numbers && outcome.pick === selectedNumbers.value.length
    );
  }
  
  return selectedMainMarketData.value.outcomes.find(outcome => 
    outcome.select_numbers && outcome.pick === selectedNumbers.value.length
  );
});

const currentOdds = computed(() => {
  return currentOutcome.value ? Number(currentOutcome.value.odds) : null;
});

const hotNumbers = computed(() => props.hotNumbers || []);
const coldNumbers = computed(() => props.coldNumbers || []);
const shouldShowHotColdActions = computed(() => {
  return !isLite.value && props.enableHotColdNumbers === true && (hotNumbers.value.length > 0 || coldNumbers.value.length > 0);
});

const currentLineMarket = computed<SelectedMarket | undefined>(() => {
  if (selectedMainMarketData.value && selectedMainMarketGroup.value && currentOutcome.value) {
    if (isColorComboMarket.value && selectedBonusColor.value && selectedNumbers.value.length > 0) {
      const selection = buildStandardBetPlusColorBonusSelection(
        selectedNumbers.value,
        currentOutcome.value
      );
      return {
        marketGroup: selectedMainMarketGroup.value,
        market: selectedMainMarketData.value,
        label: selection.label,
        odds: selection.odds,
        select_numbers: true,
        balls: selection.picks,
        outcome_id: selection.outcome_id,
        game_market_id: selection.game_market_id,
      };
    }

    return {
      marketGroup: selectedMainMarketGroup.value,
      market: selectedMainMarketData.value,
      label: formatOutcomeText(currentOutcome.value),
      odds: Number(currentOutcome.value.odds),
      select_numbers: currentOutcome.value.select_numbers,
      balls: currentOutcome.value.pick,
      outcome_id: currentOutcome.value.id.toString()
    };
  }

  if (selectedMainMarketData.value && hasAnySelection.value) {
    return undefined;
  }

  return props.selectedMarket || undefined;
});


// Set default market selection
const formatOutcomeText = (outcome: MarketOutcome): string => {
  let text = outcome.text || outcome.id.toString();
  
  // Replace {value} placeholder with specifier value
  if (text.includes('{value}') && outcome.specifier) {
    text = text.replace('{value}', outcome.specifier);
  }
  
  return text;
};

const onMainMarketChange = () => {
  // Clear all number selections when market changes
  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
  selectedBonusColor.value = null;
  lines.value = [];
  
  if (selectedMainMarket.value && selectedMainMarketData.value && selectedMainMarketGroup.value) {
    if (isColorComboMarket.value) {
      return;
    }
    // Find the first outcome with select_numbers for this market
    const firstOutcome = selectedMainMarketData.value.outcomes.find(outcome => outcome.select_numbers);
    if (firstOutcome) {
      const marketData: SelectedMarket = {
        marketGroup: selectedMainMarketGroup.value,
        market: selectedMainMarketData.value,
        label: formatOutcomeText(firstOutcome),
        odds: Number(firstOutcome.odds),
        select_numbers: firstOutcome.select_numbers,
        balls: firstOutcome.pick,
        outcome_id: firstOutcome.id.toString()
      };
      emit('select-market', marketData);
    }
  }
};

const setDefaultMainMarket = () => {
  if (allMainMarkets.value.length > 0 && !selectedMainMarket.value) {
    selectedMainMarket.value = allMainMarkets.value[0].id;
    onMainMarketChange();
  }
};

// Watch for changes in allMainMarkets to set default when data is available
watch(allMainMarkets, (newMarkets) => {
  if (newMarkets.length > 0 && !selectedMainMarket.value) {
    setDefaultMainMarket();
  }
}, { immediate: true });

// Watch for tab changes to clear main market selection when switching to bonus
watch(activeTab, (newTab) => {
  emit('tab-changed', newTab);
  if (newTab === 'bonus') {
    selectedMainMarket.value = '';
  } else if (newTab === 'main' && !selectedMainMarket.value) {
    setDefaultMainMarket();
  }
});

// Initialize default selection
setDefaultMainMarket();

const selectSameColorBalls = (market: Market, outcome: MarketOutcome, group: MarketGroup) => {
  const label = formatSameColorBetText(outcome);
  const selection = buildSameColorMarketSelection(market, outcome);

  emit('select-market', {
    marketGroup: group,
    market: market,
    label: label,
    select_numbers: outcome.select_numbers,
    balls: outcome.pick,
    ...selection,
  });
};

const updateSelectedMarketForColorCombo = () => {
  if (!selectedMainMarketData.value || !selectedMainMarketGroup.value) return;
  if (!selectedBonusColor.value || selectedNumbers.value.length === 0) return;

  const outcome = resolveColorBonusOutcome(
    selectedMainMarketData.value.outcomes,
    selectedBonusColor.value,
    selectedNumbers.value.length
  );
  if (!outcome) return;

  const selection = buildStandardBetPlusColorBonusSelection(
    selectedNumbers.value,
    outcome
  );

  emit('select-market', {
    marketGroup: selectedMainMarketGroup.value,
    market: selectedMainMarketData.value,
    label: selection.label,
    odds: selection.odds,
    select_numbers: true,
    balls: selection.picks,
    outcome_id: selection.outcome_id,
    game_market_id: selection.game_market_id,
  });
};

const selectBonusMarket = (market: Market, outcome: MarketOutcome, group: MarketGroup) => {
  // Check if this market/outcome is already selected
  const isAlreadySelected = selectedBonusMarkets.value.some(selected => 
    selected.market.id === market.id &&
    selected.outcome.id === outcome.id
  );
  
  if (isAlreadySelected) {
    // Remove from selection (toggle off)
    selectedBonusMarkets.value = selectedBonusMarkets.value.filter(selected => 
      !(selected.market.id === market.id &&
        selected.outcome.id === outcome.id)
    );
    
    // Emit remove event to parent
    emit('remove-market', {
      marketGroup: group,
      market: market,
      label: formatOutcomeText(outcome),
      odds: Number(outcome.odds),
      select_numbers: outcome.select_numbers,
      balls: outcome.pick,
      outcome_id: outcome.id.toString()
    });
  } else {
    const siblingSelections = selectedBonusMarkets.value.filter(
      (selected) => selected.market.id === market.id && selected.outcome.id !== outcome.id
    );
    for (const sibling of siblingSelections) {
      emit('remove-market', {
        marketGroup: group,
        market: sibling.market,
        label: formatOutcomeText(sibling.outcome),
        odds: Number(sibling.outcome.odds),
        select_numbers: sibling.outcome.select_numbers,
        balls: sibling.outcome.pick,
        outcome_id: sibling.outcome.id.toString(),
      });
    }
    selectedBonusMarkets.value = selectedBonusMarkets.value.filter(
      (selected) => selected.market.id !== market.id
    );
    selectedBonusMarkets.value.push({ market, outcome });

    emit('select-market', {
      marketGroup: group,
      market: market,
      label: formatOutcomeText(outcome),
      odds: Number(outcome.odds),
      select_numbers: outcome.select_numbers,
      balls: outcome.pick,
      outcome_id: outcome.id.toString()
    });
  }
};

// Removed toggleBonusGroup - bonus markets are now always visible

const isMarketSelected = (market: Market, outcome: MarketOutcome): boolean => {
  // Check if this is a bonus market selection
  if (selectedBonusMarkets.value.length > 0) {
    return selectedBonusMarkets.value.some(selected => 
      selected.market.id === market.id &&
      selected.outcome.id === outcome.id
    );
  }
  
  // Check if this is a main market selection
  if (!props.selectedMarket) return false;
  return (
    props.selectedMarket.market.id === market.id &&
    props.selectedMarket.outcome_id === outcome.id.toString()
  );
};

const formatMarketDescription = (
  marketOrDescription: Market | string,
  specifier?: string | null
): string => {
  if (typeof marketOrDescription === 'string') {
    let description = marketOrDescription || '';
    if (!description) return '';

    if (description.includes('{value}') && specifier) {
      description = description.replace('{value}', specifier);
    }

    return description;
  }

  let description = marketOrDescription?.description || '';
  if (!description) return '';

  const outcomeWithSpecifier = marketOrDescription.outcomes.find(outcome => outcome.specifier);
  if (outcomeWithSpecifier?.specifier && description.includes('{value}')) {
    description = description.replace('{value}', outcomeWithSpecifier.specifier);
  }

  return description;
};


// Number selection methods
const toggleMainIncludingBonusNumber = (n: number) => {
  const index = selectedNumbers.value.indexOf(n);
  
  if (index === -1) {
    if (selectedNumbers.value.length < maxSelectableBalls.value) {
      selectedNumbers.value.push(n);
    }
  } else {
    // Remove number and shift remaining numbers
    selectedNumbers.value.splice(index, 1);
  }
  
  // Update market selection with current number count
  if (isColorComboMarket.value) {
    updateSelectedMarketForColorCombo();
  } else {
    updateSelectedMarketForBalls(selectedNumbers.value.length);
  }
};

const updateSelectedMarketForBalls = (numberOfBalls: number) => {
  if (!selectedMainMarketData.value || isColorComboMarket.value) return;
  
  // Find the outcome that matches the number of balls selected
  const matchingOutcome = selectedMainMarketData.value.outcomes.find(outcome => 
    outcome.select_numbers && outcome.pick === numberOfBalls
  );
  
  if (matchingOutcome && selectedMainMarketGroup.value) {
    const marketData: SelectedMarket = {
      marketGroup: selectedMainMarketGroup.value,
      market: selectedMainMarketData.value,
      label: formatOutcomeText(matchingOutcome),
      odds: Number(matchingOutcome.odds),
      select_numbers: matchingOutcome.select_numbers,
      balls: matchingOutcome.pick,
      outcome_id: matchingOutcome.id.toString()
    };
    emit('select-market', marketData);
  }
};

const toggleNumber = (n: number) => {
  clearPickAction();
  if (isSameDrumMarket.value && selectedBonusNumbers.value.includes(n)) {
    return;
  }
  
  if (selectedNumbers.value.includes(n)) {
    selectedNumbers.value = selectedNumbers.value.filter(num => num !== n);
  } else if (selectedNumbers.value.length < maxSelectableBalls.value) {
    selectedNumbers.value.push(n);
  }
  
  // Update market selection with current number count
  if (isColorComboMarket.value) {
    updateSelectedMarketForColorCombo();
  } else {
    updateSelectedMarketForBalls(selectedNumbers.value.length);
  }
};

const toggleBonusNumber = (n: number) => {
  clearPickAction();
  if (isSameDrumMarket.value && selectedNumbers.value.includes(n)) {
    return;
  }
  
  if (selectedBonusNumbers.value.includes(n)) {
    selectedBonusNumbers.value = selectedBonusNumbers.value.filter(num => num !== n);
  } else {
    selectedBonusNumbers.value = [n]; // Only one bonus number allowed
  }
};

const shuffleNumbers = (numbers: number[]) => [...numbers].sort(() => Math.random() - 0.5);

const fillSelectionFromPool = (
  preferredPool: number[],
  fallbackPool: number[],
  targetCount: number,
  excludedNumbers: number[] = []
) => {
  const excluded = new Set(excludedNumbers);
  const uniqueNumbers = new Set<number>();

  shuffleNumbers(preferredPool).forEach((num) => {
    if (!excluded.has(num) && uniqueNumbers.size < targetCount) {
      uniqueNumbers.add(num);
    }
  });

  if (uniqueNumbers.size < targetCount) {
    shuffleNumbers(fallbackPool).forEach((num) => {
      if (!excluded.has(num) && uniqueNumbers.size < targetCount) {
        uniqueNumbers.add(num);
      }
    });
  }

  return Array.from(uniqueNumbers).slice(0, targetCount);
};

const getTargetSelectionCounts = (mainCountOverride?: number) => {
  const clampMainCount = (count: number) => Math.min(Math.max(count, 0), maxSelectableBalls.value);

  if (isBonusOnlyMarket.value) {
    return {
      mainCount: 0,
      bonusCount: selectedBonusNumbers.value.length > 0 ? selectedBonusNumbers.value.length : 1,
    };
  }

  if (mainCountOverride != null) {
    return {
      mainCount: clampMainCount(clampToAvailablePickCount(mainCountOverride, availablePickCounts.value)),
      bonusCount: (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0,
    };
  }

  if (selectedNumbers.value.length > 0 || selectedBonusNumbers.value.length > 0) {
    return {
      mainCount: clampMainCount(selectedNumbers.value.length > 0 ? selectedNumbers.value.length : actualBalls.value),
      bonusCount:
        selectedBonusNumbers.value.length > 0
          ? selectedBonusNumbers.value.length
          : (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0,
    };
  }

  if (lastQuickPickPattern.value) {
    return {
      mainCount: clampMainCount(lastQuickPickPattern.value.mainCount),
      bonusCount: lastQuickPickPattern.value.bonusCount,
    };
  }

  return {
    mainCount: clampMainCount(actualBalls.value),
    bonusCount: (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0,
  };
};

const applyTrendSelection = (preferredPool: number[], fallbackLabel: 'hot' | 'cold', mainCountOverride?: number) => {
  const { mainCount, bonusCount } = getTargetSelectionCounts(mainCountOverride);

  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];

  if (isBonusOnlyMarket.value) {
    const bonusPool = preferredPool.filter((n) => n <= bonusBallsMax.value);
    selectedBonusNumbers.value = fillSelectionFromPool(
      bonusPool,
      Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1),
      bonusCount
    );
    lastQuickPickPattern.value = { mainCount, bonusCount };
    return;
  }

  if (mainCount > 0) {
    const preferredMainPool = preferredPool.filter((n) => n <= actualBallsMax.value);
    selectedNumbers.value = fillSelectionFromPool(
      preferredMainPool,
      Array.from({ length: actualBallsMax.value }, (_, i) => i + 1),
      mainCount
    );
  }

  if (bonusCount > 0) {
    const preferredBonusPool = preferredPool.filter((n) => n <= bonusBallsMax.value);
    const availableBonusPool = Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1);
    selectedBonusNumbers.value = fillSelectionFromPool(
      preferredBonusPool,
      availableBonusPool,
      bonusCount,
      isSameDrumMarket.value ? selectedNumbers.value : []
    );
  }

  lastQuickPickPattern.value = { mainCount, bonusCount };
  updateSelectedMarketForBalls(selectedNumbers.value.length);

  if ((mainCount > 0 && selectedNumbers.value.length < mainCount) || (bonusCount > 0 && selectedBonusNumbers.value.length < bonusCount)) {
    console.warn(`Unable to fully apply ${fallbackLabel} number selection pattern; used best available fallback numbers.`);
  }
};

const quickPick = (mainCountOverride?: number) => {
  lastPickAction.value = 'quick';
  if (isBonusOnlyMarket.value) {
    // For bonus-only markets, maintain the same number of bonus selections
    const currentBonusCount = selectedBonusNumbers.value.length;
    const targetBonusCount = currentBonusCount > 0 ? currentBonusCount : 1;
    
    const bonusNumbers = Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1);
    const shuffledBonus = bonusNumbers.sort(() => Math.random() - 0.5);
    selectedBonusNumbers.value = shuffledBonus.slice(0, targetBonusCount);
    
    // Store pattern for bonus-only market
    lastQuickPickPattern.value = { mainCount: 0, bonusCount: targetBonusCount };
  } else {
    // For regular markets, 
    let mainCount: number;
    let bonusCount: number;
    
    // If user has existing selections, maintain the same count but clear previous selections
    if (mainCountOverride != null) {
      mainCount = clampToAvailablePickCount(mainCountOverride, availablePickCounts.value);
      bonusCount = (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0;
      selectedNumbers.value = [];
      selectedBonusNumbers.value = [];
    } else if (selectedNumbers.value.length > 0 || selectedBonusNumbers.value.length > 0) {
      mainCount = Math.min(selectedNumbers.value.length > 0 ? selectedNumbers.value.length : actualBalls.value, maxSelectableBalls.value);
      bonusCount = selectedBonusNumbers.value.length > 0 ? selectedBonusNumbers.value.length : 
                   ((requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0);
      
      // Clear previous selections before picking new ones
      selectedNumbers.value = [];
      selectedBonusNumbers.value = [];
    } else if (lastQuickPickPattern.value) {
      // Use the same pattern as the last quick pick
      mainCount = Math.min(lastQuickPickPattern.value.mainCount, maxSelectableBalls.value);
      bonusCount = lastQuickPickPattern.value.bonusCount;
    } else {
      // First time - use current market requirements
      mainCount = Math.min(actualBalls.value, maxSelectableBalls.value);
      bonusCount = (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0;
    }
    
    // Quick pick main numbers
    if (mainCount > 0) {
      const availableNumbers = Array.from({ length: actualBallsMax.value }, (_, i) => i + 1);
      const shuffled = availableNumbers.sort(() => Math.random() - 0.5);
      selectedNumbers.value = shuffled.slice(0, mainCount);
    }
    
    // Quick pick bonus numbers (if required)
    if (bonusCount > 0) {
      const bonusNumbers = Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1);
      const availableBonusNumbers = isSameDrumMarket.value 
        ? bonusNumbers.filter(num => !selectedNumbers.value.includes(num))
        : bonusNumbers;
      
      if (availableBonusNumbers.length > 0) {
        const shuffledBonus = availableBonusNumbers.sort(() => Math.random() - 0.5);
        selectedBonusNumbers.value = shuffledBonus.slice(0, bonusCount);
      } else {
        selectedBonusNumbers.value = [];
      }
    }
    
    // Store the pattern for next quick pick
    lastQuickPickPattern.value = { mainCount, bonusCount };

    if (isColorComboMarket.value) {
      selectedBonusColor.value = pickRandomBallColor();
    }
    
    // Update market selection to match the number of balls selected
    if (isColorComboMarket.value) {
      updateSelectedMarketForColorCombo();
    } else {
      updateSelectedMarketForBalls(selectedNumbers.value.length);
    }
  }

};

const buildCurrentLine = () => ({
  numbers: [...selectedNumbers.value],
  bonus: (requiresBonusBall.value || (isSameDrumMarket.value && !isMainIncludingBonusMarket.value) || isBonusOnlyMarket.value) ? selectedBonusNumbers.value[0] : undefined,
  bonusColor: isColorComboMarket.value ? selectedBonusColor.value ?? undefined : undefined,
  numBets: 1,
  picks: selectedNumbers.value.length,
  market: currentLineMarket.value
});

const finalizeLine = (line = buildCurrentLine()) => {
  lastQuickPickPattern.value = {
    mainCount: selectedNumbers.value.length,
    bonusCount: selectedBonusNumbers.value.length
  };

  if (props.streamlinedSelection) {
    emit('add-to-betslip', [line]);
  } else {
    lines.value.push(line);
    emit('add-line', line);
  }

  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
  selectedBonusColor.value = null;
};

const addLine = () => {
  if (!canAddLine.value) return;
  finalizeLine();
};

const selectHotNumbers = (mainCountOverride?: number) => {
  const pool = [...hotNumbers.value];
  if (!pool.length) return;
  lastPickAction.value = 'hot';
  applyTrendSelection(pool, 'hot', mainCountOverride);

};

const selectColdNumbers = (mainCountOverride?: number) => {
  const pool = [...coldNumbers.value];
  if (!pool.length) return;
  lastPickAction.value = 'cold';
  applyTrendSelection(pool, 'cold', mainCountOverride);

};

const bonusColorMap: Record<string, string> = {
  blue: '#3B82F6',
  brown: '#8B5E3C',
  green: '#28AA23',
  orange: '#F28C28',
  purple: '#7C3AED',
  red: '#E53935',
  yellow: '#EAB308',
};

const getBonusColorKey = (outcome: MarketOutcome) => {
  const normalized = outcome.text.trim().toLowerCase();
  return Object.keys(bonusColorMap).find((key) => normalized.startsWith(key)) || '';
};

const getBonusColorClass = (outcome: MarketOutcome) => {
  return getBonusColorKey(outcome) ? 'bonus-color-option' : '';
};

const getBonusColorStyle = (outcome: MarketOutcome) => {
  const key = getBonusColorKey(outcome);
  return key ? { '--bonus-color-swatch': bonusColorMap[key] } : {};
};

watch(selectedBonusColor, () => {
  if (isColorComboMarket.value) {
    updateSelectedMarketForColorCombo();
  }
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const removeLine = (index: number) => {
  lines.value.splice(index, 1);
  emit('remove-line', index);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const clearAll = () => {
  lines.value = [];
  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
  emit('clear-all');
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const addToBetslip = () => {
  emit('add-to-betslip', lines.value);
};

// Reset quick pick pattern when market changes
watch(selectedMainMarket, () => {
  lastQuickPickPattern.value = null;
});

watch(() => props.betslipLength, (newLength, oldLength) => {
  if (oldLength && oldLength > 0 && newLength === 0) {
    selectedBonusMarkets.value = [];
  }
});

// Reset internal state when market is cleared
watch(() => props.selectedMarket, (newMarket) => {
  if (!newMarket) {
    // Reset all internal state when market is cleared
    selectedNumbers.value = [];
    selectedBonusNumbers.value = [];
    lines.value = [];
    lastQuickPickPattern.value = null;
  }
});
</script>

<script lang="ts">
export default {
  name: "MobileBettingSection",
};
</script>

<style scoped>
.mobile-betting-section {
  --trend-hot-base: var(--colour-yellow-text-colour);
  --trend-cold-base: var(--detail-trend-cold-base, var(--colour-surface-cont-low));
  --trend-neutral-shadow: color-mix(in srgb, var(--shadow-color) 68%, transparent);
  --trend-hot-ring: color-mix(in srgb, var(--trend-hot-base) 72%, var(--colour-surface-cont-high) 28%);
  --trend-cold-ring: color-mix(in srgb, var(--trend-cold-base) 72%, var(--colour-surface-cont-high) 28%);
  --trend-hot-text: color-mix(in srgb, var(--trend-hot-base) 74%, var(--colour-base-text-colour) 26%);
  --trend-cold-text: color-mix(in srgb, var(--trend-cold-base) 74%, var(--colour-base-text-colour) 26%);
  --trend-ball-surface: color-mix(in srgb, var(--colour-surface) 88%, white 12%);
  --trend-ball-shadow: color-mix(in srgb, var(--shadow-color) 28%, transparent);
  --trend-ball-border: color-mix(in srgb, var(--colour-surface-cont) 78%, var(--colour-base-text-colour) 22%);
  --trend-quick-soft: color-mix(in srgb, var(--colour-orange-button) 14%, var(--colour-surface-cont-low) 86%);
  --trend-hot-soft: color-mix(in srgb, var(--trend-hot-base) 16%, var(--colour-surface-cont-low) 84%);
  --trend-cold-soft: color-mix(in srgb, var(--trend-cold-base) 16%, var(--colour-surface-cont-low) 84%);
  --trend-clear-soft: color-mix(in srgb, var(--colour-secondary-text-colour) 10%, var(--colour-surface-cont-low) 90%);
  background: var(--colour-surface-cont-lowest);
  border-radius: 8px;
  margin-bottom: 0;
  overflow: hidden;
  border: 1px solid var(--colour-surface-cont);
  width: 100%;
  box-sizing: border-box;
}

.mobile-betting-header {
  background: var(--surface-detail-header);
  padding: 10px var(--detail-mobile-gutter, 16px);
  border-bottom: 1px solid var(--border-detail-panel);
}

.mobile-section-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-detail-header);
  margin: 0;
}

.mobile-tab-navigation {
  display: flex;
  background: var(--surface-detail-panel);
  border-bottom: 1px solid var(--border-detail-card);
}

.mobile-tab-btn {
  flex: 1;
  padding: 8px 12px;
  background: transparent;
  border: none;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-detail-body);
  cursor: pointer;
  transition: border-color 0.2s ease;
  border-bottom: 2px solid transparent;
}

.mobile-tab-btn:hover {
  color: var(--text-detail-body);
  background: transparent;
}

.mobile-tab-btn.active {
  color: var(--text-detail-body);
  border-bottom-color: var(--colour-orange-button);
  background: transparent;
  font-weight: 700;
}

.mobile-tab-content {
  padding: var(--detail-mobile-gutter, 12px);
}

.active-tab-content {
  background: transparent;
  box-shadow: none;
}

.mobile-market-dropdown-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-dropdown-container {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-dropdown-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--colour-base-text-colour);
}

.mobile-market-dropdown {
  width: 100%;
  padding: 10px 40px 10px 12px;
  border: 1px solid var(--colour-surface-cont);
  border-radius: 8px;
  background-color: var(--colour-surface);
  color: var(--colour-base-text-colour);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: var(--detail-market-select-arrow-img);
  background-repeat: no-repeat;
  background-position: right var(--detail-market-select-arrow-offset, 16px) center;
}

.mobile-market-dropdown:hover {
  border-color: var(--colour-orange-button);
}

.mobile-market-dropdown:focus {
  outline: none;
  border-color: var(--colour-orange-button);
  box-shadow: 0 0 0 2px var(--bg-hover);
}

.mobile-selected-market-info {
  background: var(--colour-surface-cont);
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid var(--colour-surface-cont);
}

.mobile-market-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--colour-base-text-colour);
  margin-bottom: 4px;
}

.mobile-market-description {
  font-size: 12px;
  color: var(--colour-secondary-text-colour);
  line-height: 1.35;
}

.mobile-betting-group {
  margin-bottom: 16px;
  border: 1px solid var(--colour-surface-cont);
  border-radius: 8px;
  overflow: hidden;
  background: var(--colour-surface);
}

.mobile-betting-group:last-child {
  margin-bottom: 0;
}

.mobile-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: var(--colour-surface-cont);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.mobile-group-header:hover {
  background: var(--bg-hover);
}

.mobile-group-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--colour-base-text-colour);
}

.mobile-expand-btn {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.mobile-expand-btn:hover {
  background: var(--bg-hover);
}

.mobile-expand-btn img {
  width: 16px;
  height: 16px;
  filter: var(--icon-filter);
}

.mobile-group-content {
  padding: 16px;
}

.mobile-market-item {
  margin-bottom: 16px;
}

.mobile-market-item:last-child {
  margin-bottom: 0;
}

.mobile-market-header {
  margin-bottom: 12px;
}

.mobile-market-description {
  font-size: 13px;
  color: var(--colour-secondary-text-colour);
  line-height: 1.4;
}

.mobile-betting-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-bet-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--colour-surface-cont-low);
  border: 1px solid var(--colour-surface-cont);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-bet-row:hover {
  background: var(--bg-hover);
  border-color: var(--colour-orange-button);
}

.mobile-bet-row.selected {
  background: var(--colour-orange-button);
  border-color: var(--colour-orange-button);
  color: white;
}

.mobile-bet-row.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mobile-bet-row.disabled:hover {
  background: var(--colour-surface-cont-low);
  border-color: var(--colour-surface-cont);
}

.mobile-bet-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-detail-row);
}

.mobile-bet-row.selected .mobile-bet-label {
  color: var(--text-button);
}

.mobile-bet-amount {
  font-size: 14px;
  font-weight: 600;
  color: var(--colour-orange-button);
}

.mobile-bet-row.selected .mobile-bet-amount {
  color: var(--text-button);
}

/* New optimized bonus market styles */
.mobile-bonus-markets {
  padding: 12px;
}

.mobile-bonus-group {
  margin-bottom: 20px;
}

.mobile-bonus-group:last-child {
  margin-bottom: 0;
}

.mobile-bonus-group-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--colour-base-text-colour);
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--colour-surface-cont);
}

.mobile-bonus-markets-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.mobile-bonus-market-item {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mobile-bonus-market-description {
  font-size: 14px;
  font-weight: 500;
  color: var(--colour-base-text-colour);
  line-height: 1.4;
}

.mobile-bonus-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.mobile-same-color-selector {
  grid-column: 1 / -1;
  margin-bottom: 4px;
}

.mobile-bonus-bet-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--colour-surface);
  border: 2px solid var(--colour-surface-cont);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 60px;
}

.mobile-bonus-bet-card:hover {
  border-color: var(--colour-orange-button);
  background: var(--bg-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px var(--shadow-color);
}

.mobile-bonus-bet-card.selected {
  background: var(--colour-orange-button);
  border-color: var(--colour-orange-button);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px var(--bg-hover);
}

.mobile-bonus-bet-card.bonus-color-option {
  background: color-mix(in srgb, var(--bonus-color-swatch, var(--colour-surface)) 12%, var(--colour-surface) 88%);
  border-color: color-mix(in srgb, var(--bonus-color-swatch, var(--colour-surface-cont)) 38%, var(--colour-surface-cont) 62%);
}

.mobile-bonus-bet-card.bonus-color-option:hover {
  border-color: var(--bonus-color-swatch, var(--colour-orange-button));
}

.mobile-bonus-bet-card.bonus-color-option.selected {
  background: color-mix(in srgb, var(--bonus-color-swatch, var(--colour-surface)) 18%, var(--colour-surface) 82%);
  border-color: var(--bonus-color-swatch, var(--colour-orange-button));
}

.mobile-bonus-bet-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  /* gap: 4px; */
}

.mobile-bonus-bet-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--colour-base-text-colour);
  line-height: 1.3;
}

.mobile-bonus-bet-card.bonus-color-option .mobile-bonus-bet-label {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-bonus-bet-card.bonus-color-option .mobile-bonus-bet-label::before {
  content: "";
  width: 14px;
  height: 14px;
  border-radius: 999px;
  flex-shrink: 0;
  background: var(--bonus-color-swatch, var(--colour-surface-cont));
  border: 1px solid color-mix(in srgb, var(--bonus-color-swatch, var(--colour-surface-cont)) 72%, var(--colour-surface-cont-high) 28%);
}

.mobile-bonus-bet-card.selected .mobile-bonus-bet-label {
  color: white;
}

.mobile-bonus-bet-odds {
  font-size: 16px;
  font-weight: 700;
  color: var(--colour-orange-button);
}

.mobile-bonus-bet-card.selected .mobile-bonus-bet-odds {
  color: white;
}

.mobile-bonus-bet-indicator {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--colour-surface-cont);
  border: 2px solid var(--colour-surface-cont);
  transition: all 0.2s ease;
}

.mobile-bonus-bet-card.selected .mobile-bonus-bet-indicator {
  background: white;
  border-color: white;
}

.mobile-bonus-bet-check {
  font-size: 14px;
  font-weight: bold;
  color: var(--colour-secondary-text-colour);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.mobile-bonus-bet-card.selected .mobile-bonus-bet-check {
  color: var(--colour-orange-button);
  opacity: 1;
}

/* Number Selection Section */
.mobile-number-selection-section {
  margin-top: 16px;
  padding: 16px 12px 12px;
  border-top: 1px solid var(--colour-surface-cont);
  box-sizing: border-box;
}

.mobile-number-selection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--colour-base-text-colour);
  margin-bottom: 12px;
}

.mobile-number-selection-title {
  flex: 1;
}

.mobile-number-selection-odds {
  font-size: 14px;
  font-weight: 600;
  color: var(--colour-orange-button);
}

.mobile-number-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  margin: 0;
  padding: 8px;
  box-sizing: border-box;
}

@media (min-width: 375px) {
  .mobile-number-grid {
    grid-template-columns: repeat(7, 1fr);
  }
}

@media (max-width: 320px) {
  .mobile-number-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

.mobile-number-circle {
  width: 36px;
  height: 36px;
  position: relative;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
  border: 2px solid var(--trend-ball-border);
  background: var(--colour-surface-cont-lowest);
  color: var(--colour-base-text-colour);
  box-shadow: none;
}

@media (max-width: 320px) {
  .mobile-number-circle {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
}

.mobile-number-circle:hover {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--colour-orange-button) 68%, var(--trend-ball-border) 32%);
}

.mobile-number-circle.selected {
  background: var(--colour-orange-button);
  border-color: var(--colour-orange-button);
  color: var(--text-button);
}

.mobile-number-circle.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.mobile-number-circle.hot {
  border-color: color-mix(in srgb, var(--trend-hot-ring) 72%, var(--trend-ball-border) 28%);
  background: color-mix(in srgb, var(--trend-hot-base) 8%, var(--colour-surface) 92%);
}

.mobile-number-circle.cold {
  border-color: color-mix(in srgb, var(--trend-cold-ring) 72%, var(--trend-ball-border) 28%);
  background: color-mix(in srgb, var(--trend-cold-base) 8%, var(--colour-surface) 92%);
}

.mobile-number-circle.hot::after,
.mobile-number-circle.cold::after {
  position: absolute;
  top: -1px;
  right: 4px;
  width: 13px;
  height: 13px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0;
  border: 2px solid var(--colour-surface-cont-lowest);
  box-shadow: none;
}

.mobile-number-circle.hot::after {
  content: "";
  background: linear-gradient(180deg, color-mix(in srgb, var(--trend-hot-base) 86%, white 14%), color-mix(in srgb, var(--trend-hot-base) 72%, black 28%));
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='M12.7 2.6c.3 2-.3 3.5-1.7 5.2-1 1.2-1.6 2.2-1.6 3.7 0 1.8 1.3 3.1 3 3.1 2.3 0 3.8-1.9 3.8-4.3 0-1.1-.3-2.1-1.1-3.5 2.9 1.6 4.8 4.4 4.8 7.8 0 4.2-3.2 7.4-7.7 7.4-4.1 0-7.2-2.9-7.2-6.9 0-2.8 1.4-4.9 3.6-6.9 1.8-1.7 3-3.1 4.1-5.6z'/%3E%3C/svg%3E") center / 10px 10px no-repeat;
}

.mobile-number-circle.cold::after {
  content: "";
  background: var(--trend-cold-base);
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cg fill='none' stroke='white' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='M12 2v20'/%3E%3Cpath d='m4.9 6.1 14.2 11.8'/%3E%3Cpath d='m19.1 6.1-14.2 11.8'/%3E%3Cpath d='M4 12h16'/%3E%3C/g%3E%3C/svg%3E") center / 10px 10px no-repeat;
}

.mobile-number-circle.selected.hot {
  background: var(--trend-hot-base);
  border-color: var(--trend-hot-base);
}

.mobile-number-circle.selected.cold {
  background: var(--trend-cold-base);
  border-color: var(--trend-cold-base);
  color: var(--colour-text-inverse);
}

.mobile-number-circle.quick-pick {
  animation: pulse 1s infinite;
}

.mobile-number-circle.main-number {
  background: var(--colour-orange-button);
  color: white;
}

.mobile-number-circle.bonus-number {
  background: var(--colour-yellow-text-colour);
  color: white;
}

.number-type-indicator {
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 10px;
  font-weight: bold;
  background: var(--shadow-color);
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.mobile-bonus-section {
  margin-bottom: 16px;
}

.mobile-bonus-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--colour-base-text-colour);
  margin-bottom: 8px;
  text-align: center;
}

.mobile-bonus-number-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  margin: 0;
  padding: 8px;
  box-sizing: border-box;
}

@media (min-width: 375px) {
  .mobile-bonus-number-grid {
    grid-template-columns: repeat(7, 1fr);
  }
}

@media (max-width: 320px) {
  .mobile-bonus-number-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

.mobile-bonus-circle {
  background: var(--colour-surface);
  border-color: var(--colour-surface-cont);
}

.mobile-bonus-circle.selected {
  background: var(--colour-orange-button);
  border-color: var(--colour-orange-button);
  color: white;
}

/* Market Info Display */
.mobile-market-info {
  margin: 16px 0;
  display: flex;
  justify-content: center;
}

.mobile-market-display {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: var(--colour-surface-cont);
  border: 1px solid var(--colour-surface-cont);
  border-radius: 8px;
  min-width: 200px;
}

.mobile-market-display.center-layout {
  justify-content: center;
}

.mobile-market-text {
  font-size: 12px;
  font-weight: 400;
  color: var(--colour-base-text-colour);
}

.mobile-market-odds {
  font-size: 16px;
  font-weight: 700;
  color: var(--colour-orange-button);
}

.mobile-betting-actions-primary {
  margin-top: 14px;
  margin-bottom: 10px;
}

.mobile-betting-action-btn-hero {
  width: 100%;
  min-height: 40px;
  padding: 10px 12px;
  border: none;
  border-radius: 10px;
  background: color-mix(in srgb, var(--colour-orange-button) 88%, white 12%);
  color: var(--text-button);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  cursor: pointer;
  box-shadow: 0 8px 16px color-mix(in srgb, var(--shadow-color) 95%, transparent);
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease, filter 0.15s ease;
}

.mobile-betting-action-btn-hero:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.03);
  box-shadow: 0 16px 28px color-mix(in srgb, var(--shadow-color) 100%, transparent);
}

.mobile-betting-action-btn-hero:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.mobile-betting-actions-secondary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 4px;
}

.mobile-betting-action-btn-secondary {
  min-height: 36px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid var(--bg-secondary-button-border-color, var(--colour-surface-cont));
  background: var(--bg-secondary-button, var(--colour-surface-cont-low));
  color: var(--bg-secondary-button-color, var(--colour-base-text-colour));
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.16),
    0 4px 10px color-mix(in srgb, var(--shadow-color) 24%, transparent);
  transition:
    transform 0.15s ease,
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
}

.mobile-betting-action-btn-secondary:hover {
  transform: translateY(-1px);
  background: color-mix(in srgb, var(--colour-surface-cont-low) 88%, var(--bg-hover) 12%);
  box-shadow: 0 8px 14px color-mix(in srgb, var(--shadow-color) 30%, transparent);
}

.mobile-betting-action-btn-secondary:focus-visible {
  outline: 2px solid var(--colour-orange-button);
  outline-offset: 2px;
}

.mobile-betting-action-btn-secondary--quick {
  border-color: var(--bg-secondary-button-border-color, var(--colour-surface-cont));
}

.mobile-betting-action-btn-secondary--quick:hover {
  color: color-mix(in srgb, var(--colour-orange-button) 78%, var(--colour-base-text-colour) 22%);
}

.mobile-betting-action-btn-secondary--hot {
  color: var(--trend-hot-text);
}

.mobile-betting-action-btn-secondary--hot:hover {
  color: color-mix(in srgb, var(--trend-hot-base) 86%, var(--colour-base-text-colour) 14%);
}

.mobile-betting-action-btn-secondary--cold {
  color: var(--trend-cold-base);
}

.mobile-betting-action-btn-secondary--cold:hover {
  color: color-mix(in srgb, var(--trend-cold-base) 86%, var(--colour-base-text-colour) 14%);
}

.mobile-betting-action-btn-secondary--clear {
  color: color-mix(in srgb, var(--colour-base-text-colour) 88%, var(--colour-secondary-text-colour) 12%);
}

.mobile-betting-action-btn-secondary--clear:hover {
  color: var(--colour-base-text-colour);
}

.betting-action-btn-icon {
  width: 12px;
  height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.betting-action-btn-icon svg {
  width: 12px;
  height: 12px;
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .mobile-betting-section {
    background: var(--colour-surface-cont-lowest);
  }

  .mobile-betting-header {
    background: var(--colour-surface-cont-low);
  }
  
  .mobile-betting-group {
    background: var(--colour-surface);
  }
  
  .mobile-group-header {
    background: var(--colour-surface-cont);
  }
  
  .mobile-bet-row {
    background: var(--colour-surface-cont-low);
  }
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .mobile-betting-header {
    padding: 10px 12px;
  }
  
  .mobile-section-title {
    font-size: 14px;
  }
  
  .mobile-betting-groups {
    padding: 12px 16px;
  }
  
  .mobile-group-header {
    padding: 12px;
  }
  
  .mobile-group-title {
    font-size: 13px;
  }
  
  .mobile-group-content {
    padding: 12px;
  }
  
  .mobile-bet-row {
    padding: 10px 12px;
  }
  
  .mobile-bet-label {
    font-size: 13px;
  }
  
  .mobile-bet-amount {
    font-size: 13px;
  }
  
  /* Bonus market responsive styles */
  .mobile-bonus-markets {
    padding: 0px;
  }
  
  .mobile-bonus-options {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }
  
  .mobile-bonus-bet-card {
    padding: 10px 12px;
    min-height: 30px;
  }
  
  .mobile-bonus-bet-label {
    font-size: 12px;
  }
  
  .mobile-bonus-bet-odds {
    font-size: 14px;
  }
  
  .mobile-bonus-bet-indicator {
    width: 20px;
    height: 20px;
  }
  
  .mobile-bonus-bet-check {
    font-size: 12px;
  }
  
  .mobile-tab-content {
    padding: 12px 8px;
  }
  
  .mobile-tab-btn {
    padding: 10px 12px;
    font-size: 13px;
  }
  
  .mobile-market-dropdown {
    padding: 10px 40px 10px 12px;
    font-size: 13px;
    background-position: right var(--detail-market-select-arrow-offset, 14px) center;
  }
  
  .mobile-dropdown-label {
    font-size: 13px;
  }
  
  .mobile-selected-market-info {
    padding: 12px;
  }
  
  .mobile-market-title {
    font-size: 14px;
  }
  
  .mobile-market-description {
    font-size: 13px;
  }
}
</style>
